// @flow
import { I18nManager } from "react-native";
import { i18n } from "../config/i18n";
import { getLocales } from "./mocks";
import moment from "moment";
import * as yup from "yup";
import type { Locale } from "../types";

const defaultLoginFormValues = {
  telephone: "",
  password: "",
};

const defaultForgotPasswordFormValues = {
  email: "",
};

const getYupEmail = (locale: string) =>
  yup
    .string()
    .email(i18n.t("loginScreen.errors.validEmail", { locale }))
    .required(i18n.t("loginScreen.errors.requiredEmail", { locale }));

const getYupFirstName = (locale: string) =>
  yup
    .string()
    .required(i18n.t("loginScreen.errors.requiredFirstName", { locale }));

const getYupLastName = () => yup.string();
const getYupTelephone = (locale: string) =>
  yup
    .string()
    .required(i18n.t("loginScreen.errors.requiredTelephone", { locale }))
    .matches(
      /^(00966|00971)5\d{8}$/,
      i18n.t("loginScreen.errors.invalidKSAPhoneNumber", { locale }),
    );

const getYupPassword = (locale: string) =>
  yup
    .string()
    .min(8, i18n.t("loginScreen.errors.passwordEightChar", { locale }))
    .required(i18n.t("loginScreen.errors.requiredPassword", { locale }));

const getYupForgotPasswordForm = (locale: string) =>
  yup.object().shape({
    email: getYupEmail(locale),
  });

const getYupLoginForm = (locale: string) =>
  yup.object().shape({
    telephone: getYupTelephone(locale),
    password: getYupPassword(locale),
  });

const getYupAccountForm = (locale: string) =>
  yup.object().shape({
    firstName: getYupFirstName(locale),
    lastName: getYupLastName(),
    email: getYupEmail(locale),
    telephone: getYupTelephone(locale),
  });

const getYupPaymentCardholderName = (locale: string) =>
  yup
    .string()
    .required(
      i18n.t("payWithCardScreen.errors.cardholderNameRequired", { locale }),
    )
    .matches(
      /^[a-zA-Z ]+$/,
      i18n.t("payWithCardScreen.errors.cardholderNameValid"),
    );

const getYupPaymentCardNumber = (locale: string, isSavedCard?: boolean) => {
  const cardNumberValidation = yup
    .string()
    .transform((value) => value.replace(/\s/g, ""))
    .required(i18n.t("payWithCardScreen.errors.cardNumberRequired", { locale }))
    .min(9, i18n.t("payWithCardScreen.errors.cardNumberInvalid", { locale }))
    .max(19, i18n.t("payWithCardScreen.errors.cardNumberInvalid", { locale }));

  return isSavedCard
    ? cardNumberValidation
    : cardNumberValidation.matches(
        /^\d*$/,
        i18n.t("payWithCardScreen.errors.cardNumberInvalid", { locale }),
      );
};

const getYupPaymentExpiresMonth = (locale: string) =>
  yup
    .string()
    .required(
      i18n.t("payWithCardScreen.errors.expiresMonthRequired", { locale }),
    )
    .length(
      2,
      i18n.t("payWithCardScreen.errors.expiresMonthLength", { locale }),
    )
    .matches(
      /(0[0-9]|1[012])/,
      i18n.t("payWithCardScreen.errors.expiresMonthInvalid", { locale }),
    );

const getYupPaymentExpiresYear = (locale: string) =>
  yup
    .string()
    .required(
      i18n.t("payWithCardScreen.errors.expiresYearRequired", { locale }),
    )
    .length(2, i18n.t("payWithCardScreen.errors.expiresYearLength", { locale }))
    .matches(
      /([2-9][0-9])/,
      i18n.t("payWithCardScreen.errors.expiresYearInvalid", { locale }),
    );

const getYupPaymentCVV = (locale: string) =>
  yup
    .string()
    .required(i18n.t("payWithCardScreen.errors.cvvRequired", { locale }))
    .min(3, i18n.t("payWithCardScreen.errors.cvvInvalid", { locale }))
    .max(4, i18n.t("payWithCardScreen.errors.cvvInvalid", { locale }))
    .matches(
      /^\d*$/,
      i18n.t("payWithCardScreen.errors.cvvInvalid", { locale }),
    );

const getYupPayWithCardForm = (locale: string, isSavedCard?: boolean) =>
  yup.object().shape({
    cardholderName: getYupPaymentCardholderName(locale),
    cardNumber: getYupPaymentCardNumber(locale, isSavedCard),
    expiresMonth: getYupPaymentExpiresMonth(locale),
    expiresYear: getYupPaymentExpiresYear(locale),
    cvv: getYupPaymentCVV(locale),
  });

const getYupRegisterForm = (locale: string) =>
  yup.object().shape({
    firstName: getYupFirstName(locale),
    lastName: getYupLastName(),
    telephone: getYupTelephone(locale),
    email: getYupEmail(locale),
    password: getYupPassword(locale),
  });

const getYupCVVModalForm = (locale: string) =>
  yup.object().shape({
    cvv: getYupPaymentCVV(locale),
  });

const getDateTime = (unixTimeStamp: number): { date: string; time: string } => {
  const date = moment.unix(unixTimeStamp).format("DD/MM/YYYY");
  const time = moment.unix(unixTimeStamp).format("h:mm A");
  return { date, time };
};

const getCurrentLocale = (): Locale => {
  const locales = getLocales();
  if (locales && locales.length > 0) {
    const { languageCode } = locales[0];
    const matchedLanguageCode = languageCode.match(/^[a-z]{2}/);
    if (matchedLanguageCode) {
      return matchedLanguageCode[0] === "ar" ? "ar" : "en";
    }
  }
  return "en";
};

const isRTL = (locale: Locale) => {
  const currentLocale = locale || getCurrentLocale();
  return (
    currentLocale.indexOf("he") === 0 ||
    currentLocale.indexOf("ar") === 0 ||
    I18nManager.isRTL
  );
};

export {
  defaultLoginFormValues,
  defaultForgotPasswordFormValues,
  getYupLoginForm,
  getYupForgotPasswordForm,
  getYupPayWithCardForm,
  getYupRegisterForm,
  getYupCVVModalForm,
  isRTL,
  getCurrentLocale,
  getDateTime,
  getYupAccountForm,
};
